export const headerDescription = [
    {h: 'Dsp', d: 'Name of Dsp'},
    {h: 'Publisher', d: 'Name of Publisher'},
    {h: 'Date', d: 'Event date'},
    {h: 'Domain', d: 'The domain from which the request came'},
    {h: 'Device type', d: 'Type of device from which the request came'},
    {h: 'Requests', d: 'Requests from publisher'},
    {h: 'Ad Slot Requests', d: 'Requests from each ad slot'},
    {h: 'Bids', d: 'Dsp bids'},
    {h: 'Wins', d: 'The event occurs when the banner wins on Prebid.JS auction'},
    {h: 'Impressions', d: 'Count of impressions(load impression on page) to each ad slot'},
    {h: 'Revenue', d: 'Revenue'},
    {h: 'Views', d: 'Banner is displayed on the screen of the device'},
    {h: 'Clicks', d: 'User click to impression on page'},
    {h: 'Vast Start', d: 'Vast start playing'},
    {h: 'Vast First Q', d: 'Vast played first quartile'},
    {h: 'Vast First Q', d: 'Vast played first quartile'},
    {h: 'Vast Mid', d: 'Vast played midpoint'},
    {h: 'Vast Third Q', d: 'Vast played third quartile'},
    {h: 'Vast Compl', d: 'Vast completed'},
    {h: 'Vast Error', d: 'Vast player error'},
    {h: 'Spendings', d: 'Spendings'},
    {h: 'eCPM', d: 'Average CPM'},
    {h: 'CTR', d: 'Click Through Rate - Average % of clicks generated across impressions delivered'},
    {h: 'CPC', d: 'Cost Per Click'},
    {h: 'VTR', d: 'View Through Rate - % of users who have watched a video the whole way through'},
    {h: 'Format Size', d: 'Size of impression'},
    {h: 'Campaign Id', d: 'Campaign Id'},
    {h: 'creative id', d: 'creative id'},
    {h: 'Spend (Net)', d: 'Gross - commission'}
]

type SortOrder = 'ascend' | 'descend';

const isDateKey = (key: string) =>
    key.includes('date') || key.includes('time');

const compareValues = (aVal: unknown, bVal: unknown, key: string): number => {
    if (isDateKey(key)) {
        const aT = Date.parse(String(aVal));
        const bT = Date.parse(String(bVal));
        if (!isNaN(aT) && !isNaN(bT)) return aT - bT;
    }
    const aNum = parseFloat(String(aVal).replace(/,/g, ''));
    const bNum = parseFloat(String(bVal).replace(/,/g, ''));
    if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
    return String(aVal ?? '').localeCompare(String(bVal ?? ''));
};

const GROUP_BY_HEADER_ALIASES: Record<string, string[]> = {
    size: ['format size'],
    mediatype: ['media type'],
    devicetype: ['device type'],
    dealid: ['deal id', 'deal'],
};

const groupByToHeaderKey = (
    groupBy: string,
    header: string[],
): string | undefined => {
    const normalized = groupBy.toLowerCase();
    const exact = header.find((h) => h.toLowerCase() === normalized);
    if (exact) return exact.toLowerCase();

    for (const alias of GROUP_BY_HEADER_ALIASES[normalized] ?? []) {
        const match = header.find((h) => h.toLowerCase() === alias);
        if (match) return match.toLowerCase();
    }
    return undefined;
};

const ALWAYS_FILTERABLE_COLUMNS = ['publisher', 'dsp'];

/** Filterable keys: Publisher, Dsp, and group-by columns present in the table header. */
export const getReportFilterableKeys = (
    header: string[] | undefined,
    groupByColumnNames: string[],
): Set<string> => {
    if (!header) return new Set();
    const keys = new Set<string>();
    for (const col of ALWAYS_FILTERABLE_COLUMNS) {
        const key = groupByToHeaderKey(col, header);
        if (key) keys.add(key);
    }
    for (const gb of groupByColumnNames) {
        const key = groupByToHeaderKey(gb, header);
        if (key) keys.add(key);
    }
    return keys;
};

export const getReportColumnLabel = (
    header: string[] | undefined,
    key: string,
): string =>
    header?.find((h) => h.toLowerCase() === key) ??
    key.charAt(0).toUpperCase() + key.slice(1);

const normalizeColumnKey = (key: string) => key.toLowerCase().replace(/\s+/g, '');

const isDateLikeValue = (value: string) =>
    /\d{4}-\d{2}-\d{2}/.test(value) || /\d{1,2}:\d{2}(:\d{2})?/.test(value);

const isSummableValueColumn = (
    key: string,
    rawValues: string[],
    valueColumnKeys: Set<string>,
): boolean => {
    if (isDateKey(key) || !valueColumnKeys.has(normalizeColumnKey(key))) return false;
    if (rawValues.some((v) => isDateLikeValue(v.trim()))) return false;
    const nums = rawValues.map((v) => parseFloat(v.replace(/[$%,\s]/g, '')));
    return nums.length > 0 && nums.every((n) => !isNaN(n));
};

/** Rebuilds the total row after column filters; sums value/metric columns only. */
export const buildFilteredReportTotalRow = (
    header: string[],
    filteredRows: Record<string, any>[],
    valueColumnNames: string[],
): Record<string, any> => {
    const valueColumnKeys = new Set(
        valueColumnNames.map((c) => normalizeColumnKey(c)),
    );
    const lowerKeys = header.map((h) => h.toLowerCase());
    const totalRow: Record<string, any> = { _isTotalRow: true };

    lowerKeys.forEach((key, index) => {
        if (index === 0) {
            totalRow[key] = 'Total';
            return;
        }
        const rawValues = filteredRows.map((r) => String(r[key] ?? ''));
        if (!isSummableValueColumn(key, rawValues, valueColumnKeys)) {
            totalRow[key] = '';
            return;
        }
        const nums = rawValues.map((v) => parseFloat(v.replace(/[$%,\s]/g, '')));
        const sum = nums.reduce((a, b) => a + b, 0);
        const formatted = Number.isInteger(sum)
            ? sum.toLocaleString('en-US')
            : parseFloat(sum.toFixed(6)).toLocaleString('en-US');
        const hasDollar = rawValues.some((v) => v.trim().startsWith('$'));
        const hasPercent = rawValues.some((v) => v.trim().endsWith('%'));
        totalRow[key] = hasDollar
            ? `$ ${formatted}`
            : hasPercent
              ? `${formatted}%`
              : formatted;
    });

    return totalRow;
};

export const sortReportRows = <T extends Record<string, any>>(
    rows: T[],
    key: string | null,
    order: SortOrder | null,
): T[] => {
    if (!key || !order) return rows;
    const totalRow = rows.find(r => r._isTotalRow);
    const dataRows = rows.filter(r => !r._isTotalRow);
    const sorted = [...dataRows].sort((a, b) => {
        const cmp = compareValues(a[key], b[key], key);
        return order === 'ascend' ? cmp : -cmp;
    });
    return totalRow ? [...sorted, totalRow] : sorted;
};